import { apiGet, apiPatch } from '../../../shared/api-client/index.js';
import type { SalesHealthOverview } from '../../../../src/sales-health/overview.js';

export type { SalesHealthOverview };

export interface DashboardScenarioRunSignal {
  id: string;
  status: string;
  scenarioId: string;
  createdAt: string;
  finishedAt: string | null;
}

export interface DashboardPendingApprovalSignal {
  id: string;
  status: string;
  kind: string;
  createdAt: string;
  expiresAt: string | null;
  /** Entity the approval concerns (from the server-owned presentation). */
  entityKind?: string;
  entityLabel?: string;
}

export interface DashboardSignals {
  scenarioRuns: DashboardScenarioRunSignal[];
  pendingApprovals: DashboardPendingApprovalSignal[];
}

export interface DashboardStats {
  dealsTotal: number;
  wonThisMonth: number;
  hotLeadsCount: number;
  avgDealValue: number;
}

export interface DashboardActivity {
  id: string;
  kind: string;
  actor: string;
  subject: string | null;
  occurredAt: string;
  payload: Record<string, unknown>;
  deal: { id: string; title: string } | null;
  contact: { id: string; firstName: string; lastName: string | null } | null;
}

export interface DashboardOnboardingState {
  id: string;
  userId: string;
  workspaceId: string;
  completedSteps: string[];
  dismissed: boolean;
  updatedAt: string;
}

export type ManagerBriefingPriorityKind = 'meetings' | 'overdue_tasks' | 'risk_deals' | 'hot_leads';

export interface ManagerBriefingPriority {
  kind: ManagerBriefingPriorityKind;
  count?: number;
}

export interface ManagerBriefingTask {
  id: string;
  title: string;
  dealTitle: string | null;
  contactName: string | null;
  companyName: string | null;
  dueAt: string | null;
  priority: string;
  status: string;
  assigneeName: string | null;
  createdByName: string | null;
}

export interface ManagerBriefingRiskDeal {
  id: string;
  title: string;
  reason: string;
}

export interface ManagerBriefingMeeting {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string;
  dealTitle: string | null;
  contactName: string | null;
}

export interface ManagerBriefingHotLead {
  id: string;
  title: string;
  reason: string;
}

export interface ManagerBriefingPipeline {
  new: number;
  contacted: number;
  negotiation: number;
  won: number;
  lost: number;
  conversionRate: number;
}

export interface ManagerBriefing {
  hotLeads: ManagerBriefingHotLead[];
  overdueTasks: ManagerBriefingTask[];
  riskDeals: ManagerBriefingRiskDeal[];
  todayMeetings: ManagerBriefingMeeting[];
  pipeline: ManagerBriefingPipeline;
  priorities: ManagerBriefingPriority[];
}

export function getDashboardBriefingQueryKey(
  workspaceId: string,
): readonly [string, string, string] {
  return ['dashboard', workspaceId, 'briefing'] as const;
}

export async function getDashboardBriefing(): Promise<ManagerBriefing> {
  return apiGet<ManagerBriefing>('/dashboard/briefing');
}

export function getDashboardSalesHealthQueryKey(
  workspaceId: string,
): readonly [string, string, string] {
  return ['dashboard', workspaceId, 'sales-health'] as const;
}

export async function getDashboardSalesHealth(): Promise<SalesHealthOverview> {
  return apiGet<SalesHealthOverview>('/dashboard/sales-health');
}

export function getDashboardSignalsQueryKey(
  workspaceId: string,
): readonly [string, string, string] {
  return ['dashboard', workspaceId, 'signals'] as const;
}

export async function getDashboardSignals(): Promise<DashboardSignals> {
  return apiGet<DashboardSignals>('/dashboard/signals');
}

export function getDashboardStatsQueryKey(workspaceId: string): readonly [string, string, string] {
  return ['dashboard', workspaceId, 'stats'] as const;
}

export async function getDashboardStats(): Promise<DashboardStats> {
  return apiGet<DashboardStats>('/dashboard/stats');
}

/** Daily KPI trend series (60 buckets) — real deltas and sparklines (spec D). */
export interface DashboardKpiSeriesPoint {
  date: string;
  value: number;
}

export interface DashboardKpiSeries {
  revenueByDay: DashboardKpiSeriesPoint[];
  newDealsByDay: DashboardKpiSeriesPoint[];
  newContactsByDay: DashboardKpiSeriesPoint[];
}

export function getDashboardKpiSeriesQueryKey(
  workspaceId: string,
): readonly [string, string, string] {
  return ['dashboard', workspaceId, 'kpi-series'] as const;
}

export async function getDashboardKpiSeries(): Promise<DashboardKpiSeries> {
  return apiGet<DashboardKpiSeries>('/dashboard/kpi-series');
}

export function getDashboardActivityQueryKey(
  workspaceId: string,
): readonly [string, string, string] {
  return ['dashboard', workspaceId, 'activity'] as const;
}

export async function getDashboardActivity(limit: number = 10): Promise<DashboardActivity[]> {
  return apiGet<DashboardActivity[]>(`/dashboard/activity?limit=${limit}`);
}

export function getDashboardOnboardingQueryKey(
  workspaceId: string,
): readonly [string, string, string] {
  return ['dashboard', workspaceId, 'onboarding'] as const;
}

export async function getDashboardOnboarding(): Promise<DashboardOnboardingState> {
  return apiGet<DashboardOnboardingState>('/dashboard/onboarding');
}

export async function updateDashboardOnboarding(data: {
  completedSteps?: string[];
  dismissed?: boolean;
}): Promise<DashboardOnboardingState> {
  // apiPatch сам сериализует тело: лишняя обёртка { body } уходила на сервер
  // как {"body":{…}}, и поля терялись — состояние онбординга не сохранялось.
  return apiPatch<DashboardOnboardingState>('/dashboard/onboarding', data);
}
